import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { addToCart } from '../../../redux/actions/cartActions';

const ProductDetailsPage = () => {
  const dispatch = useDispatch();
  const cart = useSelector(state => state.cart);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [selectedSize, setSelectedSize] = useState('standard');
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedStandardSize, setSelectedStandardSize] = useState(null);
  const [selectedMeasurements, setSelectedMeasurements] = useState({});
  const [customSize, setCustomSize] = useState('');
  const [sizeError, setSizeError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const queryColor = queryParams.get('color');
  
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('http://localhost:4000/products/getallproducts/');
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        setProducts(data);
        if (data && data.length > 0) {
          setSelectedProduct(data[0]);
          // Get up to 6 related products
          setRelatedProducts(data.slice(0, 6));
          
          if (queryColor && data[0].images) {
            const colorIndex = data[0].colors?.findIndex(c => c.toLowerCase() === queryColor.toLowerCase());
            if (colorIndex !== -1 && colorIndex < data[0].images.length) {
              setSelectedColor(colorIndex);
              setCurrentImageIndex(colorIndex);
            }
          }
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchProducts();
  }, [queryColor]);

  // Reset size selections when changing size type
  useEffect(() => {
    setSelectedStandardSize(null);
    setSelectedMeasurements({});
    setCustomSize('');
    setSizeError('');
  }, [selectedSize]);

  // Log selected values
  useEffect(() => {
    console.log('Selected Size:', selectedSize);
    console.log('Selected Color:', selectedColor);
    console.log('Selected Product:', selectedProduct);
  }, [selectedSize, selectedColor, selectedProduct]);

  if (loading) return <div className="flex justify-center items-center h-screen">Loading products...</div>;
  if (error) return <div className="flex justify-center items-center h-screen text-red-600">Error: {error}</div>;
  if (!selectedProduct) return <div className="flex justify-center items-center h-screen">No products found</div>;

  const handleStandardSizeSelect = (size) => {
    setSelectedStandardSize(size);
    setSizeError('');
  };

  const handleSizeTypeChange = (type) => {
    setSelectedSize(type);
    setSizeError('');
  };

  const handleMeasurementChange = (part, value) => {
    setSelectedMeasurements(prev => ({
      ...prev,
      [part]: value
    }));
    setSizeError('');
  };

  const handleCustomSizeChange = (e) => {
    setCustomSize(e.target.value);
    setSizeError('');
  };

  const validateSizeSelection = () => {
    switch(selectedSize) {
      case 'standard':
        if (!selectedStandardSize) {
          setSizeError('Please select a standard size');
          toast.error('Please select a standard size');
          return false;
        }
        break;
      case 'select':
        if (selectedProduct.customizationId?.selectSize) {
          const requiredMeasurements = Object.entries(selectedProduct.customizationId.selectSize)
            .filter(([_, options]) => options.length > 0)
            .map(([part]) => part);

          const missingMeasurements = requiredMeasurements.filter(
            part => !selectedMeasurements[part]
          );

          if (missingMeasurements.length > 0) {
            setSizeError(`Please select measurements for: ${missingMeasurements.join(', ')}`);
            toast.error('Please select all required measurements');
            return false;
          }
        }
        break;
      case 'custom':
        if (!customSize.trim()) {
          setSizeError('Please enter your custom measurements');
          toast.error('Please enter your custom measurements');
          return false;
        }
        break;
    }
    return true;
  };

  const getSizeData = () => {
    switch(selectedSize) {
      case 'standard':
        return {
          type: 'standard',
          size: selectedStandardSize
        };
      case 'select':
        return {
          type: 'select',
          measurements: selectedMeasurements
        };
      case 'custom':
        return {
          type: 'custom',
          measurements: customSize
        };
      default:
        return null;
    }
  };

  const handleAddToCart = () => {
    if (!validateSizeSelection()) {
      return;
    }

    try {
      const cartItem = {
        id: selectedProduct._id,
        name: selectedProduct.productName,
        price: selectedProduct.sellingPrice,
        image: selectedProduct.images?.[currentImageIndex],
        quantity: 1,
        size: selectedSize === 'standard' 
          ? selectedStandardSize 
          : selectedSize === 'select' 
            ? selectedMeasurements 
            : customSize,
        measurements: selectedSize === 'select' ? selectedMeasurements : null
      };

      dispatch(addToCart(cartItem));
      toast.success('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add product to cart');
    }
  };

  const handleBuyNow = () => {
    if (!validateSizeSelection()) {
      return;
    }

    try {
      const cartItem = {
        id: selectedProduct._id,
        name: selectedProduct.productName,
        price: selectedProduct.sellingPrice,
        image: selectedProduct.images?.[currentImageIndex],
        quantity: 1,
        size: selectedSize === 'standard' 
          ? selectedStandardSize 
          : selectedSize === 'select' 
            ? selectedMeasurements 
            : customSize,
        measurements: selectedSize === 'select' ? selectedMeasurements : null
      };

      dispatch(addToCart(cartItem));
      navigate('/cart');
    } catch (error) {
      console.error('Error in buy now:', error);
      toast.error('Failed to process your order');
    }
  };

  const handlePrevImage = () => {
    if (!selectedProduct.images || selectedProduct.images.length === 0) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? selectedProduct.images.length - 1 : prevIndex - 1
    );
    setSelectedColor((prevIndex) => 
      prevIndex === 0 ? selectedProduct.images.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    if (!selectedProduct.images || selectedProduct.images.length === 0) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === selectedProduct.images.length - 1 ? 0 : prevIndex + 1
    );
    setSelectedColor((prevIndex) => 
      prevIndex === selectedProduct.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const renderSizeSelector = () => {
    if (selectedSize === 'standard') {
      return (
        <div className="flex flex-wrap gap-4">
          {selectedProduct.standardSize?.map(size => (
            <button 
              key={size} 
              className={`w-16 h-16 border-2 rounded-lg flex items-center justify-center text-lg font-medium transition-colors
                ${selectedStandardSize === size 
                  ? 'border-purple-600 bg-purple-50 text-purple-600' 
                  : 'border-gray-200 hover:border-purple-600'}`}
              onClick={() => handleStandardSizeSelect(size)}
            >
              {size}
            </button>
          )) || <div>No standard sizes available</div>}
        </div>
      );
    } else if (selectedSize === 'select' && selectedProduct.customizationId?.selectSize) {
      const selectSize = selectedProduct.customizationId.selectSize;
      return (
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-medium">How to Measure</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(selectSize).map(([part, options]) => (
                <div key={part} className="space-y-1">
                  <div className="flex items-center gap-1">
                    <span>{part.charAt(0).toUpperCase() + part.slice(1)}</span>
                    <span className="w-5 h-5 rounded-full border flex items-center justify-center text-xs">i</span>
                  </div>
                  <div className="relative">
                    <select 
                      className="w-full border p-2 pr-8 appearance-none"
                      onChange={(e) => console.log(`${part} selected:`, e.target.value)}
                    >
                      <option value="">Select</option>
                      {options.map((option, idx) => (
                        <option key={idx} value={option}>{option}</option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    } else if (selectedSize === 'custom') {
      return (
        <div className="space-y-6">
          <div className="border p-4">
            <h3 className="font-medium mb-2">Your Measurements</h3>
            <input 
              type="text" 
              placeholder="Enter your measurements here..." 
              className="w-full p-2 bg-gray-50 text-gray-400"
              onChange={(e) => console.log('Custom measurements:', e.target.value)}
            />
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomizeModal = () => {
    if (!selectedProduct.customizationId?.customizationType) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCustomizeModal(false)}></div>
        <div className="relative bg-white w-full max-w-4xl max-h-screen overflow-auto">
          <button 
            className="absolute top-4 right-4 text-2xl"
            onClick={() => setShowCustomizeModal(false)}
          >
            ✕
          </button>
          
          <div className="flex flex-col md:flex-row h-full">
            <div className="w-full md:w-1/2 p-4">
              <div className="grid grid-cols-2 gap-4">
                {selectedProduct.customizationId.customizationType.map((customType, index) => (
                  <div key={index} className="space-y-4">
                    <h3 className="text-lg font-medium">{customType.typeName}</h3>
                    <img 
                      src={customType.standardImage} 
                      alt={customType.typeName}
                      className="w-full h-64 object-contain"
                    />
                    <div className="space-y-2">
                      {customType.options.map((option, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id={`option-${index}-${idx}`} 
                            className="w-4 h-4"
                            onChange={(e) => console.log(`${customType.typeName} - ${option}:`, e.target.checked)}
                          />
                          <label htmlFor={`option-${index}-${idx}`}>{option}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="w-full md:w-1/2 p-6 space-y-6">
              <h2 className="text-xl font-bold">CUSTOMIZATION OPTIONS</h2>
              
              <div className="mt-8">
                <h3 className="text-xl font-medium">Select Your Style</h3>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {selectedProduct.customizationId.customizationType.map((customType, index) => (
                    <div key={index} className="border p-4 rounded-lg">
                      <h4 className="font-medium mb-2">{customType.typeName}</h4>
                      <img 
                        src={customType.standardImage} 
                        alt={customType.typeName}
                        className="w-full h-48 object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-auto pt-4 border-t">
                <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:gap-4">
                  <button 
                    className="border px-6 py-3 w-full"
                    onClick={() => console.log('Style saved')}
                  >
                    Save Style
                  </button>
                  <button 
                    className="bg-purple-600 text-white px-6 py-3 w-full"
                    onClick={() => {
                      console.log('Selected customization:', selectedProduct.customizationId.customizationType);
                      setShowCustomizeModal(false);
                    }}
                  >
                    Select style & proceed
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="flex justify-center items-center h-screen">Loading products...</div>;
  if (error) return <div className="flex justify-center items-center h-screen text-red-600">Error: {error}</div>;
  if (!selectedProduct) return <div className="flex justify-center items-center h-screen">No products found</div>;

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm mb-6">
        <a href="/" className="hover:underline">Home</a>
        <span>/</span>
        <a href="#" className="hover:underline">{selectedProduct.categoryName}</a>
        <span>/</span>
        <span>{selectedProduct.variantName}</span>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Product Images Section - Fixed */}
        <div className="w-full lg:w-3/5 sticky top-0 h-fit">
          <div className="flex gap-4 h-[600px]">
            {/* Thumbnails */}
            <div className="w-24 h-full space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 pr-2">
              {selectedProduct.images && selectedProduct.images.length > 0 ? (
                selectedProduct.images.map((img, idx) => (
                  <div 
                    key={idx} 
                    className={`w-20 h-20 border rounded-lg overflow-hidden cursor-pointer transition-all
                      ${idx === currentImageIndex ? 'ring-2 ring-purple-500 border-transparent' : 'border-gray-200 hover:border-purple-300'}`}
                    onClick={() => {
                      setCurrentImageIndex(idx);
                      console.log('Selected image:', idx);
                    }}
                  >
                    <img 
                      src={img} 
                      alt={`View ${idx + 1}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))
              ) : (
                <div className="w-20 h-20 border rounded-lg">
                  <img src="/api/placeholder/80/80" alt="Placeholder" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            
            {/* Main Image */}
            <div className="flex-1 relative">
              <img 
                src={selectedProduct.images && selectedProduct.images.length > 0 
                  ? selectedProduct.images[currentImageIndex] 
                  : "/api/placeholder/600/800"} 
                alt={selectedProduct.productName} 
                className="w-full h-full object-contain"
              />
              
              {/* Navigation Arrows */}
              <button 
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:bg-white"
                onClick={handlePrevImage}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button 
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:bg-white"
                onClick={handleNextImage}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {/* Share Button */}
              <button className="absolute top-4 right-4 bg-white w-10 h-10 rounded-full flex items-center justify-center shadow-md">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Customize Style Button - Reduced size */}
          {selectedProduct.customization && (
            <div className="mt-4">
              <button 
                className="w-48 mx-auto border border-purple-600 text-purple-600 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-purple-50 transition-colors text-sm"
                onClick={() => setShowCustomizeModal(true)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Customize Style
              </button>
            </div>
          )}
        </div>
        
        {/* Product Info Section */}
        <div className="w-full lg:w-2/5">
          <div className="space-y-6"> {/* Removed pb-32 since buttons won't be fixed */}
            <h1 className="text-2xl font-medium">{selectedProduct.productName}</h1>
            
            <div className="flex items-center gap-4">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold">₹{selectedProduct.sellingPrice?.toFixed(2)}</span>
                {selectedProduct.actualPrice !== selectedProduct.sellingPrice && (
                  <span className="text-gray-500 line-through text-lg">₹{selectedProduct.actualPrice?.toFixed(2)}</span>
                )}
              </div>
              {selectedProduct.actualPrice !== selectedProduct.sellingPrice && (
                <span className="text-red-600 text-lg font-medium">
                  -{Math.round((1 - selectedProduct.sellingPrice / selectedProduct.actualPrice) * 100)}% OFF
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex text-yellow-400">
                {'★★★★☆'.split('').map((star, i) => (
                  <span key={i}>{star}</span>
                ))}
              </div>
              <span className="text-gray-600">(72 reviews)</span>
            </div>
            
            {/* Size Selection Section */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-lg">SELECT SIZE</h3>
              
              {sizeError && (
                <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">
                  {sizeError}
                </div>
              )}

              {selectedProduct.customization ? (
                <div className="space-y-6">
                  {/* Size Type Selection */}
                  <div className="flex gap-4">
                    <button
                      onClick={() => handleSizeTypeChange('standard')}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                        selectedSize === 'standard'
                          ? 'border-purple-600 bg-purple-50 text-purple-600'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          selectedSize === 'standard' ? 'border-purple-600' : 'border-gray-400'
                        }`}>
                          {selectedSize === 'standard' && (
                            <div className="w-2 h-2 rounded-full bg-purple-600" />
                          )}
                        </div>
                        <span>Standard size</span>
                      </div>
                    </button>

                    <button
                      onClick={() => handleSizeTypeChange('select')}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                        selectedSize === 'select'
                          ? 'border-purple-600 bg-purple-50 text-purple-600'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          selectedSize === 'select' ? 'border-purple-600' : 'border-gray-400'
                        }`}>
                          {selectedSize === 'select' && (
                            <div className="w-2 h-2 rounded-full bg-purple-600" />
                          )}
                        </div>
                        <span>Select Size</span>
                      </div>
                    </button>

                    <button
                      onClick={() => handleSizeTypeChange('custom')}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                        selectedSize === 'custom'
                          ? 'border-purple-600 bg-purple-50 text-purple-600'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          selectedSize === 'custom' ? 'border-purple-600' : 'border-gray-400'
                        }`}>
                          {selectedSize === 'custom' && (
                            <div className="w-2 h-2 rounded-full bg-purple-600" />
                          )}
                        </div>
                        <span>Custom Size</span>
                      </div>
                    </button>
                  </div>

                  {/* Size Options Based on Selection */}
                  {selectedSize === 'standard' && (
                    <div className="space-y-4">
                      <h3 className="font-medium">Select Standard Size</h3>
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                        {selectedProduct?.standardSizes?.map((size) => (
                          <button
                            key={size}
                            onClick={() => handleStandardSizeSelect(size)}
                            className={`w-16 h-16 border-2 rounded-lg flex items-center justify-center text-lg font-medium transition-colors
                              ${selectedStandardSize === size 
                                ? 'border-purple-600 bg-purple-50 text-purple-600' 
                                : 'border-gray-200 hover:border-purple-600'}`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedSize === 'select' && selectedProduct.customizationId?.selectSize && (
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <a href="#" className="text-purple-600 hover:underline text-sm flex items-center gap-1">
                          <span>How to Measure</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </a>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {Object.entries(selectedProduct.customizationId.selectSize)
                            .filter(([_, options]) => options.length > 0)
                            .map(([part, options]) => (
                              <div key={part} className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <span className="capitalize">{part}</span>
                                  <div className="group relative">
                                    <span className="w-4 h-4 rounded-full border inline-flex items-center justify-center text-xs cursor-help">i</span>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                                      How to measure your {part}
                                    </div>
                                  </div>
                                </div>
                                <div className="relative">
                                  <select 
                                    className={`w-full border-2 p-2 pr-8 rounded-lg appearance-none transition-colors
                                      ${selectedMeasurements[part] 
                                        ? 'border-purple-500' 
                                        : 'border-gray-200 focus:border-purple-500'}`}
                                    value={selectedMeasurements[part] || ''}
                                    onChange={(e) => handleMeasurementChange(part, e.target.value)}
                                  >
                                    <option value="">Select {part}</option>
                                    {options.map((option, idx) => (
                                      <option key={idx} value={option}>{option}</option>
                                    ))}
                                  </select>
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSize === 'custom' && (
                    <div className="space-y-4">
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-2">Custom Size Instructions</h4>
                        <p className="text-sm text-gray-600">
                          Please provide your measurements in inches, separated by commas
                        </p>
                      </div>
                      <div className="relative">
                        <textarea
                          value={customSize}
                          onChange={(e) => handleCustomSizeChange(e.target.value)}
                          placeholder="Enter your measurements..."
                          className={`w-full border-2 p-3 rounded-lg min-h-[100px] transition-colors
                            ${customSize 
                              ? 'border-purple-500' 
                              : 'border-gray-200 focus:border-purple-500'}`}
                        />
                      </div>
                    </div>
                  )}

                  {/* Size Guide Link */}
                  <div className="flex justify-end">
                    <button className="text-purple-600 text-sm hover:underline flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                      </svg>
                      Size Guide
                    </button>
                  </div>
                </div>
              ) : (
                // Standard size selection when customization is false
                <div className="space-y-4">
                  <h3 className="font-medium">Select Standard Size</h3>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                    {selectedProduct?.standardSizes?.map((size) => (
                      <button
                        key={size}
                        onClick={() => handleStandardSizeSelect(size)}
                        className={`w-16 h-16 border-2 rounded-lg flex items-center justify-center text-lg font-medium transition-colors
                          ${selectedStandardSize === size 
                            ? 'border-purple-600 bg-purple-50 text-purple-600' 
                            : 'border-gray-200 hover:border-purple-600'}`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Delivery Section - Updated UI */}
            <div className="space-y-4 pt-6 border-t">
              <div className="flex flex-col gap-4">
                <h3 className="font-medium text-lg">Delivery Information</h3>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="relative flex-1 w-full">
                    <input 
                      type="text" 
                      placeholder="Enter pincode" 
                      className="w-full border-2 border-gray-200 px-4 py-3 rounded-lg focus:border-purple-500 focus:outline-none transition-colors"
                    />
                    <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 transition-colors">
                      Check
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 bg-green-50 p-4 rounded-lg">
                  <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-800">Delivery by 14 Nov, Thursday</span>
                </div>
              </div>
            </div>
            
            {/* Product Details - Updated UI */}
            <div className="space-y-4 pt-6 border-t">
              <h3 className="font-medium text-lg">Product Details</h3>
              <div className="grid gap-4 bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-500 text-sm">Product Name</p>
                      <p className="font-medium">{selectedProduct.productName}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Product Code</p>
                      <p className="font-medium">#{selectedProduct.productCode}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Category</p>
                      <p className="font-medium">{selectedProduct.categoryName}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-500 text-sm">Status</p>
                      <p className="font-medium flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        {selectedProduct.status}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Stock</p>
                      <p className="font-medium">{selectedProduct.stock} units</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Tax</p>
                      <p className="font-medium">{selectedProduct.tax}%</p>
                    </div>
                  </div>
                </div>
                {selectedProduct.description && (
                  <div className="pt-4 border-t">
                    <p className="text-gray-500 text-sm">Description</p>
                    <p className="mt-1">{selectedProduct.description}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons - Not fixed anymore */}
            <div className="space-y-3 pt-6">
              <button 
                className="w-full bg-purple-600 text-white py-4 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                onClick={handleBuyNow}
              >
                BUY NOW
              </button>
              <button 
                className="w-full border-2 border-gray-300 py-4 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                onClick={handleAddToCart}
              >
                ADD TO CART
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Frequently Bought Together */}
      <div className="mt-16 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium">Frequently Bought Together</h2>
          <a href="#" className="text-blue-600 hover:underline">See More</a>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {relatedProducts.map((product) => (
            <div 
              key={product._id} 
              className="group cursor-pointer"
              onClick={() => {
                setSelectedProduct(product);
                setCurrentImageIndex(0);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <div className="aspect-[3/4] mb-3 overflow-hidden rounded-lg">
                <img 
                  src={product.images?.[0] || "/api/placeholder/300/400"} 
                  alt={product.productName} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="space-y-1">
                <h3 className="font-medium text-sm line-clamp-2">{product.productName}</h3>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold">₹{product.sellingPrice?.toFixed(2)}</span>
                  {product.customization && (
                    <span className="text-xs text-purple-600">Customizable</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {showCustomizeModal && <CustomizeModal />}
    </div>
  );
};

export default ProductDetailsPage;